// src/pages/driverReports/carSheets/buildSheets/autoDriverCosts.js
// -----------------------------------------------------------------------------
// Driver cost automation for car sheets.
//
// Safe hybrid rule:
// - If a sheet has configured main-driver assignment cost, overwrite only the
//   MONTHLY FIXED COST row with the calculated/adjusted amount.
// - If no configured assignment cost exists for the SAME car + SAME sheet driver,
//   leave the existing copied/fetched MONTHLY FIXED COST row untouched.
// - Second driver cost is applied only when a configured second-driver
//   assignment overlaps the sheet period.
// - Manual relief/discount/override records are month-specific.
// - Optional asOfDate is preview-only.
// -----------------------------------------------------------------------------

import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../../../firebase/firebase";
import { applyAssignmentCostPlanForMonth } from "../../assignmentCostPlanHistory";

const DAY_MS = 24 * 60 * 60 * 1000;

function toDate(value) {
  try {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === "function") return value.toDate();
    if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
    if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function startOfDay(value) {
  const d = toDate(value);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function endOfDay(value) {
  const d = toDate(value);
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function formatDate(value) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function daysInMonth(month) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return 30;
  return new Date(y, m, 0).getDate();
}

function inclusiveDays(from, to) {
  const s = startOfDay(from);
  const e = startOfDay(to);
  if (!s || !e || e < s) return 0;
  return Math.floor((e.getTime() - s.getTime()) / DAY_MS) + 1;
}

function overlapRange(aStart, aEnd, bStart, bEnd) {
  const s1 = startOfDay(aStart);
  const e1 = endOfDay(aEnd);
  const s2 = startOfDay(bStart);
  const e2 = endOfDay(bEnd);
  if (!s1 || !e1 || !s2 || !e2) return null;

  const s = new Date(Math.max(s1.getTime(), s2.getTime()));
  const e = new Date(Math.min(e1.getTime(), e2.getTime()));
  if (e < s) return null;
  return { start: startOfDay(s), end: startOfDay(e), days: inclusiveDays(s, e) };
}

function minDate(a, b) {
  const da = toDate(a);
  const db = toDate(b);
  if (!da) return db;
  if (!db) return da;
  return da.getTime() <= db.getTime() ? da : db;
}

function round2(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function money(value) {
  return Number(value || 0).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function norm(value) {
  return String(value || "").trim().toUpperCase().replace(/\s+/g, " ");
}

function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function safeIdPart(value) {
  return String(value || "")
    .trim()
    .replace(/[/#?\s]+/g, "_")
    .replaceAll("[", "_")
    .replaceAll("]", "_")
    .slice(0, 80);
}

function ensureExpenseRows(sheet) {
  if (!Array.isArray(sheet.expensesRows)) sheet.expensesRows = [];
}

function getExpenseRow(sheet, label) {
  ensureExpenseRows(sheet);
  const key = norm(label);
  let row = sheet.expensesRows.find((r) => norm(r.label) === key);
  if (!row) {
    row = {
      id: `exp_${safeIdPart(label)}_${Date.now()}`,
      label,
      col1: "",
      col2: "",
      col3: "",
      total: 0,
    };
    sheet.expensesRows.push(row);
  }
  return row;
}

function getAssignmentRole(a) {
  const raw = String(a?.driverRole || a?.assignmentRole || a?.role || "").toLowerCase();
  if (raw.includes("second")) return "second";
  if (raw.includes("main")) return "main";
  return "";
}

function isConfigured(a) {
  return a?.costConfigured === true || a?.installmentMode === "auto";
}

function sameDriver(a, b) {
  const aId = String(a?.driverId || "").trim();
  const bId = String(b?.driverId || "").trim();
  if (aId && bId) return aId === bId;

  const aName = norm(a?.driverName || a?.name || "");
  const bName = norm(b?.driverName || b?.name || "");
  return Boolean(aName && bName && aName === bName);
}

function hasConfiguredSecondDriverPlan(assignment) {
  if (getAssignmentRole(assignment) === "second" && isConfigured(assignment)) return true;

  return (Array.isArray(assignment?.costPlanHistory) ? assignment.costPlanHistory : []).some(
    (plan) => getAssignmentRole(plan) === "second" && isConfigured(plan)
  );
}

function isSameCar(a, sheet) {
  if (!a || !sheet) return false;
  if (a.vehicleId && sheet.vehicleId && String(a.vehicleId) === String(sheet.vehicleId)) return true;

  const aCar = norm(a.vehicleNumber || a.carNumber || "");
  const sCar = norm(sheet.carNumber || sheet.vehicleNumber || "");
  if (aCar && sCar && aCar === sCar) return true;

  // Your car numbers can be stored like: 57208 Private L, 57208 Private L,
  // L 57208 Private, etc. Match by the numeric plate portion as fallback.
  const aDigits = digitsOnly(a.vehicleNumber || a.carNumber || "");
  const sDigits = digitsOnly(sheet.carNumber || sheet.vehicleNumber || "");
  return Boolean(aDigits && sDigits && aDigits === sDigits);
}

function flattenAssignments(assignmentsMap) {
  const out = [];
  Object.entries(assignmentsMap || {}).forEach(([driverId, list]) => {
    (list || []).forEach((a) => out.push({ ...a, driverId: a.driverId || driverId }));
  });
  return out;
}

function monthIndex(month) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return null;
  return y * 12 + (m - 1);
}

function monthDiff(fromMonth, toMonth) {
  const from = monthIndex(fromMonth);
  const to = monthIndex(toMonth);
  if (from === null || to === null) return 0;
  return to - from;
}

function dateToMonth(value) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function getInstallmentInfo(assignment, month) {
  const enabled =
    assignment?.installmentPlanEnabled === true ||
    Number(assignment?.installmentTotalCount || 0) > 0 ||
    Number(assignment?.installmentCurrentNo || 0) > 0;

  if (!enabled) {
    return { label: "", detail: "", currentNo: 0, totalCount: 0, remaining: 0 };
  }

  const totalCount = Number(assignment.installmentTotalCount || 0);
  const baseNo = Number(assignment.installmentCurrentNo || 0);
  const baseMonth =
    assignment.installmentBaseMonth ||
    dateToMonth(assignment.installmentEffectiveFrom || assignment.startDate) ||
    month;

  if (!baseNo || !baseMonth) {
    return {
      label: "",
      detail: "Installment plan enabled but current number/base month is missing.",
      currentNo: 0,
      totalCount,
      remaining: 0,
    };
  }

  const currentNo = baseNo + monthDiff(baseMonth, month);
  const remaining = totalCount ? Math.max(totalCount - currentNo, 0) : 0;
  const label = totalCount ? `${currentNo}/${totalCount}` : `${currentNo}`;

  return { label, detail: "", currentNo, totalCount, remaining };
}

function getSheetDriverKeys(sheet) {
  const keys = new Set();
  const add = (value) => {
    const k = norm(value);
    if (k) keys.add(k);
  };

  Object.entries(sheet?.driverPeriods || {}).forEach(([driverId, periods]) => {
    add(driverId);
    const list = Array.isArray(periods) ? periods : [periods];
    list.forEach((p) => {
      add(p?.driverId);
      add(p?.driverName);
      add(p?.name);
    });
  });

  (sheet?.driverIds || []).forEach(add);
  (sheet?.driverNames || []).forEach(add);
  (sheet?.drivers || []).forEach(add);
  add(sheet?.driverId);
  add(sheet?.driverName);
  return keys;
}

function assignmentBelongsToSheetDriver(assignment, sheet) {
  const keys = getSheetDriverKeys(sheet);
  if (!keys.size) return true; // fallback for old sheets without driverPeriods
  return keys.has(norm(assignment.driverId)) || keys.has(norm(assignment.driverName));
}

function assignmentOverlapsSheet(assignment, sheet, asOfDate = null) {
  const calcPeriodEnd = asOfDate ? minDate(sheet.periodEnd, asOfDate) : sheet.periodEnd;
  const effectiveStart = assignment.installmentEffectiveFrom || assignment.startDate;
  const r = overlapRange(
    sheet.periodStart,
    calcPeriodEnd,
    effectiveStart,
    assignment.endDate || calcPeriodEnd
  );
  return Boolean(r && r.days > 0);
}

function adjustmentMatches(adj, sheet, assignment, month) {
  if (!adj || !sheet || !assignment) return false;
  if (String(adj.month || "") !== String(month || "")) return false;
  if (adj.status && String(adj.status).toLowerCase() !== "active") return false;

  if (adj.assignmentId && String(adj.assignmentId) === String(assignment.id)) return true;

  if (adj.vehicleId && assignment.vehicleId && String(adj.vehicleId) !== String(assignment.vehicleId)) {
    return false;
  }

  if (!adj.vehicleId && adj.vehicleNumber) {
    if (digitsOnly(adj.vehicleNumber) !== digitsOnly(assignment.vehicleNumber || sheet.carNumber || "")) {
      return false;
    }
  }

  if (adj.driverId && String(adj.driverId) !== String(assignment.driverId || "")) {
    return false;
  }

  return Boolean(adj.assignmentId || adj.driverId || adj.vehicleId || adj.vehicleNumber);
}

const DEFAULT_WPS_FEE_PER_DRIVER = 25;

function isWpsEligibleSheet(sheet) {
  if (!sheet) return false;
  if (sheet.isCarryForwardOnly) return false;
  if (sheet.isManualDriverSheet) return false;
  if (String(sheet.carNumber || "").toUpperCase().startsWith("MANUAL-")) return false;
  if (!sheet.periodStart || !sheet.periodEnd) return false;
  return true;
}

function getSheetDriverWorkItems(sheet) {
  const items = [];
  const driverPeriods = sheet?.driverPeriods || {};

  Object.entries(driverPeriods).forEach(([driverId, periods]) => {
    (Array.isArray(periods) ? periods : []).forEach((period) => {
      const driverName = period?.driverName || period?.name || "";
      const range = overlapRange(
        sheet.periodStart,
        sheet.periodEnd,
        period?.startDate || sheet.periodStart,
        period?.endDate || sheet.periodEnd
      );
      if (!range || range.days <= 0) return;

      items.push({
        driverKey: String(driverId || driverName || "").trim(),
        driverName: driverName || String(driverId || ""),
        sheet,
        days: range.days,
        periodStart: range.start,
        periodEnd: range.end,
      });
    });
  });

  if (!items.length && Array.isArray(sheet?.drivers)) {
    const days = inclusiveDays(sheet.periodStart, sheet.periodEnd);
    (sheet.drivers || []).forEach((name) => {
      const driverName = String(name || "").trim();
      if (!driverName || !days) return;
      items.push({
        driverKey: norm(driverName),
        driverName,
        sheet,
        days,
        periodStart: startOfDay(sheet.periodStart),
        periodEnd: startOfDay(sheet.periodEnd),
      });
    });
  }

  return items.filter((x) => x.driverKey && x.days > 0);
}

function chooseBestWpsSheetForDriver(items) {
  if (!items?.length) return null;

  return [...items].sort((a, b) => {
    if (Number(b.days || 0) !== Number(a.days || 0)) return Number(b.days || 0) - Number(a.days || 0);

    const bEnd = toDate(b.periodEnd)?.getTime() || 0;
    const aEnd = toDate(a.periodEnd)?.getTime() || 0;
    if (bEnd !== aEnd) return bEnd - aEnd;

    const bStart = toDate(b.periodStart)?.getTime() || 0;
    const aStart = toDate(a.periodStart)?.getTime() || 0;
    return bStart - aStart;
  })[0];
}

export function applyWpsChargesToSheets({ sheets, feePerDriver = DEFAULT_WPS_FEE_PER_DRIVER }) {
  const sheetList = Array.isArray(sheets) ? sheets : [];
  const eligibleSheets = sheetList.filter(isWpsEligibleSheet);

  const workByDriver = new Map();

  eligibleSheets.forEach((sheet) => {
    getSheetDriverWorkItems(sheet).forEach((item) => {
      const key = norm(item.driverKey);
      if (!workByDriver.has(key)) {
        workByDriver.set(key, {
          driverName: item.driverName,
          items: [],
        });
      }
      workByDriver.get(key).items.push(item);
    });
  });

  const driversBySheetId = new Map();

  workByDriver.forEach((driverData) => {
    const best = chooseBestWpsSheetForDriver(driverData.items);
    if (!best?.sheet) return;
    const sheetId = best.sheet.id || `${best.sheet.carNumber}|${best.sheet.periodLabel || ""}`;
    if (!driversBySheetId.has(sheetId)) driversBySheetId.set(sheetId, []);
    driversBySheetId.get(sheetId).push({
      driverName: driverData.driverName || best.driverName,
      days: best.days,
    });
  });

  eligibleSheets.forEach((sheet) => {
    const sheetId = sheet.id || `${sheet.carNumber}|${sheet.periodLabel || ""}`;
    const drivers = driversBySheetId.get(sheetId) || [];
    const row = getExpenseRow(sheet, "WPS CHARGES");
    const count = drivers.length;
    const total = round2(count * Number(feePerDriver || 0));

    row.total = total;
    row.col1 = "";
    row.col2 = "";
    row.col3 = "";
    row.autoGenerated = true;
    row.autoWpsCharge = true;
    row.description = "";
    row.autoDetails = count
      ? `Auto WPS: ${count} driver(s) × AED ${money(feePerDriver)}. Drivers: ${drivers.map((x) => x.driverName).filter(Boolean).join(" | ")}. If a driver worked in multiple cars, WPS is charged only to the sheet where the driver worked the most days.`
      : "Auto WPS: no eligible assigned driver for this sheet.";
  });

  return sheetList;
}

export async function loadInstallmentAdjustments(month) {
  if (!month) return [];

  try {
    const qRef = query(collection(db, "driverInstallmentAdjustments"), where("month", "==", month));
    const snap = await getDocs(qRef);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error("Failed to load installment adjustments:", err);
    return [];
  }
}

function calculateMainCost({ sheet, month, assignment, adjustments, asOfDate }) {
  const monthDays = daysInMonth(month);
  const monthly = Number(assignment.monthlyInstallment || 0);
  const calcPeriodEnd = asOfDate ? minDate(sheet.periodEnd, asOfDate) : sheet.periodEnd;
  const plan = getInstallmentInfo(assignment, month);

  const sheetOverlap = overlapRange(
    sheet.periodStart,
    calcPeriodEnd,
    assignment.installmentEffectiveFrom || assignment.startDate,
    assignment.endDate || calcPeriodEnd
  );

  if (!sheetOverlap || !monthDays) {
    return {
      assignment,
      baseAmount: 0,
      finalAmount: 0,
      details: [
        asOfDate
          ? `As of ${formatDate(asOfDate)} there are no active cost days in this sheet period.`
          : "No active cost days in this sheet period.",
      ],
      monthly,
      days: 0,
      installmentLabel: plan.label,
      installmentDetail: plan.detail,
    };
  }

  const dailyRate = monthly / monthDays;
  const baseAmount = round2(dailyRate * sheetOverlap.days);
  let finalAmount = baseAmount;
  let wasOverridden = false;

  const details = [
    `Base AED ${money(monthly)} / ${monthDays} days × ${sheetOverlap.days} days = AED ${money(baseAmount)}`,
  ];

  if (asOfDate) details.unshift(`Preview as of ${formatDate(asOfDate)}`);

  const matchedAdjustments = (adjustments || []).filter((adj) => adjustmentMatches(adj, sheet, assignment, month));

  matchedAdjustments.forEach((adj) => {
    const type = String(adj.adjustmentType || adj.type || "").toLowerCase();
    const adjStart = adj.fromDate || adj.date || `${month}-01`;
    const adjEnd = adj.toDate || adj.fromDate || adj.date || calcPeriodEnd;
    const range = overlapRange(sheetOverlap.start, sheetOverlap.end, adjStart, adjEnd);
    if (!range) return;

    const adjAmount = Number(adj.amount || 0);
    const label = adj.reason || adj.remarks || type || "Adjustment";

    if (type === "manual_override") {
      finalAmount = round2(adjAmount);
      wasOverridden = true;
      details.push(`Override: AED ${money(adjAmount)} (${label})`);
      return;
    }

    if (wasOverridden) {
      details.push(`Skipped after override: ${label}`);
      return;
    }

    if (type === "days_relief" || type === "relief_by_days") {
      const relief = round2(dailyRate * range.days);
      finalAmount = round2(finalAmount - relief);
      details.push(`Relief ${range.days} days: -AED ${money(relief)} (${label})`);
      return;
    }

    if (type === "extra_charge") {
      finalAmount = round2(finalAmount + Math.abs(adjAmount));
      details.push(`Extra: +AED ${money(Math.abs(adjAmount))} (${label})`);
      return;
    }

    const discount = Math.abs(adjAmount);
    finalAmount = round2(finalAmount - discount);
    details.push(`Discount: -AED ${money(discount)} (${label})`);
  });

  return {
    assignment,
    baseAmount,
    finalAmount: round2(finalAmount),
    details,
    monthly,
    days: sheetOverlap.days,
    installmentLabel: plan.label,
    installmentDetail: plan.detail,
  };
}

function calculateSecondDriverCost({
  sheet,
  month,
  assignment,
  asOfDate,
  mainAssignments = [],
}) {
  const monthDays = daysInMonth(month);
  const monthly = Number(assignment.secondDriverMonthlyExpense || 500);
  const calcPeriodEnd = asOfDate ? minDate(sheet.periodEnd, asOfDate) : sheet.periodEnd;
  const sheetOverlap = overlapRange(
    sheet.periodStart,
    calcPeriodEnd,
    assignment.startDate,
    assignment.endDate || calcPeriodEnd
  );

  if (!sheetOverlap || !monthDays) {
    return {
      assignment,
      amount: 0,
      days: 0,
      excludedMainDays: 0,
      details: asOfDate
        ? `As of ${formatDate(asOfDate)}, ${assignment.driverName || "Second driver"} has no active second-driver days in this sheet period.`
        : `${assignment.driverName || "Second driver"}: no active days in this sheet period`,
    };
  }

  // A driver can move from Second -> Main on the same car during a month.
  // Never charge the same person as ADDITIONAL DRIVER on days when that
  // person is also assigned as Main Driver. This also protects against a
  // one-day date overlap when the old assignment ends on the same date that
  // the new main assignment starts.
  const sameDriverMainAssignments = (mainAssignments || []).filter((main) =>
    sameDriver(main, assignment)
  );

  let chargeableDays = 0;
  let excludedMainDays = 0;

  for (
    let cursor = startOfDay(sheetOverlap.start);
    cursor && cursor <= startOfDay(sheetOverlap.end);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
  ) {
    const isMainThatDay = sameDriverMainAssignments.some((main) => {
      const mainRange = overlapRange(
        cursor,
        cursor,
        main.startDate,
        main.endDate || calcPeriodEnd
      );
      return Boolean(mainRange && mainRange.days > 0);
    });

    if (isMainThatDay) excludedMainDays += 1;
    else chargeableDays += 1;
  }

  const amount = round2((monthly / monthDays) * chargeableDays);
  const exclusionText = excludedMainDays
    ? `; ${excludedMainDays} overlapping main-driver day(s) excluded`
    : "";

  return {
    assignment,
    amount,
    days: chargeableDays,
    excludedMainDays,
    details: `${assignment.driverName || "Second driver"}: AED ${money(monthly)} / ${monthDays} days × ${chargeableDays} second-driver day(s) = AED ${money(amount)}${exclusionText}`,
  };
}

export function applyDriverCostAutomationToSheet({
  sheet,
  month,
  assignmentsMap,
  adjustments = [],
  asOfDate = null,
}) {
  if (!sheet || sheet.isCarryForwardOnly || !sheet.periodStart || !sheet.periodEnd) return sheet;

  // Resolve the assignment cost terms for the statement month before any
  // cost calculation. Old assignments without costPlanHistory are returned
  // unchanged, so the existing behavior stays fully backward compatible.
  const sameCarAssignments = flattenAssignments(assignmentsMap)
    .map((a) => applyAssignmentCostPlanForMonth(a, month))
    .filter((a) => isSameCar(a, sheet));
  if (!sameCarAssignments.length) return sheet;

  // MAIN DRIVER COST MUST MATCH THE DRIVER ON THIS EXACT SHEET.
  // This prevents a later 2-day assignment of the same car from controlling
  // an earlier sheet of another driver on the same car.
  const configuredMainAssignments = sameCarAssignments.filter(
    (a) =>
      getAssignmentRole(a) === "main" &&
      isConfigured(a) &&
      assignmentBelongsToSheetDriver(a, sheet) &&
      assignmentOverlapsSheet(a, sheet, asOfDate)
  );

  const mainCosts = configuredMainAssignments
    .map((a) => calculateMainCost({ sheet, month, assignment: a, adjustments, asOfDate }))
    .filter((x) => Number(x.days || 0) > 0);

  if (mainCosts.length > 0) {
    const total = round2(mainCosts.reduce((sum, x) => sum + Number(x.finalAmount || 0), 0));
    const installmentLabels = Array.from(
      new Set(mainCosts.map((x) => x.installmentLabel).filter(Boolean))
    );
    const row = getExpenseRow(sheet, "MONTHLY FIXED COST");
    row.total = total;
    row.col1 = installmentLabels.join(" | ");
    row.col2 = "";
    row.col3 = "";
    row.autoGenerated = true;
    row.autoDriverCost = true;
    row.installmentNo = installmentLabels.join(" | ");
    row.description = "";
    row.autoDetails = mainCosts.flatMap((x) => x.details).join("; ");
  }

  const configuredSecondAssignments = sameCarAssignments.filter(
    (a) =>
      getAssignmentRole(a) === "second" &&
      isConfigured(a) &&
      assignmentOverlapsSheet(a, sheet, asOfDate)
  );

  // Main-role history is used only to prevent the same person from being
  // charged as both Main and Additional Driver on overlapping dates. The main
  // assignment does not need a configured installment for this safeguard.
  const mainRoleAssignments = sameCarAssignments.filter(
    (a) => getAssignmentRole(a) === "main" && assignmentOverlapsSheet(a, sheet, asOfDate)
  );

  const secondCosts = configuredSecondAssignments
    .map((a) =>
      calculateSecondDriverCost({
        sheet,
        month,
        assignment: a,
        asOfDate,
        mainAssignments: mainRoleAssignments,
      })
    )
    .filter((x) => Number(x.days || 0) > 0);

  // Once a car has a configured second-driver plan/history, this automation is
  // authoritative for the ADDITIONAL DRIVER row. This is important because old
  // car sheets / one-time carry-forward can otherwise leave a stale AED 500
  // row after the second driver is unassigned or promoted to main.
  const secondDriverAutomationOwned = sameCarAssignments.some(
    hasConfiguredSecondDriverPlan
  );

  if (secondCosts.length > 0 || secondDriverAutomationOwned) {
    const total = round2(secondCosts.reduce((sum, x) => sum + Number(x.amount || 0), 0));
    const row = getExpenseRow(sheet, "ADDITIONAL DRIVER");
    row.total = total;
    row.col1 = "";
    row.col2 = "";
    row.col3 = "";
    row.autoGenerated = true;
    row.autoSecondDriverCost = true;
    row.description = "";
    row.autoDetails = secondCosts.length
      ? secondCosts.map((x) => x.details).join("; ")
      : "No configured second-driver cost applies to this sheet period.";
  }

  if (asOfDate) {
    sheet.installmentAsOfDatePreview = formatDate(asOfDate);
  }

  return sheet;
}

export function applyDriverCostAutomationToSheets({
  sheets,
  month,
  assignmentsMap,
  adjustments = [],
  asOfDate = null,
  includeWps = false,
  wpsFeePerDriver = DEFAULT_WPS_FEE_PER_DRIVER,
}) {
  const sheetList = (sheets || []).map((sheet) =>
    applyDriverCostAutomationToSheet({ sheet, month, assignmentsMap, adjustments, asOfDate })
  );

  if (includeWps) {
    applyWpsChargesToSheets({ sheets: sheetList, feePerDriver: wpsFeePerDriver });
  }

  return sheetList;
}
